import { io } from 'socket.io-client';
import { socketPath, wsApi } from '../urlConfig';

const SOCKET_CLIENT_ID_KEY = 'ao_user_socket_client_id';
const SOCKET_AUTH_TIMEOUT_MS = 5000;
const SOCKET_LIFECYCLE_MARK = '__AO_USER_SOCKET_LIFECYCLE_V2__';

let socketInstance = null;
let socketClientId = '';
let authenticatedSocketId = '';
let authenticatedToken = '';
let authPromise = null;

const createClientId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return `client_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

const readStoredClientId = () => {
    if (typeof window === 'undefined') {
        return socketClientId || createClientId();
    }

    if (window.__USER_SOCKET_CLIENT_ID__) {
        return String(window.__USER_SOCKET_CLIENT_ID__);
    }

    try {
        const stored = window.sessionStorage.getItem(SOCKET_CLIENT_ID_KEY);
        if (stored) {
            window.__USER_SOCKET_CLIENT_ID__ = stored;
            return stored;
        }
    } catch (_error) { }

    const next = createClientId();
    window.__USER_SOCKET_CLIENT_ID__ = next;

    try {
        window.sessionStorage.setItem(SOCKET_CLIENT_ID_KEY, next);
    } catch (_error) { }

    return next;
};

export const getSocketClientId = () => {
    if (!socketClientId) socketClientId = readStoredClientId();
    return socketClientId;
};

const getToken = () => {
    if (typeof window === 'undefined') return '';
    return window.localStorage.getItem('token') || '';
};

const getSocketConfigKey = () => `${wsApi}|${socketPath}`;

const resetAuthenticatedState = () => {
    authenticatedSocketId = '';
    authenticatedToken = '';
    authPromise = null;
};

const clearGlobalSocket = () => {
    if (typeof window === 'undefined') return;
    delete window.userSocket;
    delete window.__USER_SOCKET__;
    delete window.__USER_SOCKET_CONFIG_KEY__;
};

const adoptExistingWindowSocket = (socketConfigKey) => {
    if (typeof window === 'undefined') return null;

    const existing = window.__USER_SOCKET__;
    const existingConfigKey = window.__USER_SOCKET_CONFIG_KEY__;

    if (!existing || existingConfigKey !== socketConfigKey) return null;
    return existing;
};

const applySocketAuth = (socket) => {
    if (!socket) return false;

    const token = getToken();
    const clientId = getSocketClientId();
    const previousToken = String(socket.auth?.token || '');

    socket.auth = {
        ...(socket.auth || {}),
        token: token || undefined,
        clientId,
    };

    socket.io.opts.query = {
        ...(socket.io.opts.query || {}),
        clientId,
    };

    // userId больше не передаём с клиента как источник identity.
    delete socket.io.opts.query.userId;

    return previousToken !== token;
};

const authenticateConnectedSocket = (socket, { force = false } = {}) => {
    if (!socket?.connected) return Promise.resolve(false);

    const token = getToken();

    if (!token) {
        resetAuthenticatedState();
        return Promise.resolve(false);
    }

    applySocketAuth(socket);

    if (
        !force &&
        authenticatedSocketId === socket.id &&
        authenticatedToken === token
    ) {
        socket.emit('user:join');
        return Promise.resolve(true);
    }

    if (authPromise) return authPromise;

    const socketIdAtStart = socket.id;

    const pending = new Promise((resolve) => {
        let settled = false;

        const finish = (value) => {
            if (settled) return;
            settled = true;
            resolve(value);
        };

        const timer = window.setTimeout(() => {
            console.warn('User socket auth:token timeout');
            finish(false);
        }, SOCKET_AUTH_TIMEOUT_MS);

        socket.emit('auth:token', { token }, (response = {}) => {
            window.clearTimeout(timer);

            if (!socket.connected || socket.id !== socketIdAtStart) {
                finish(false);
                return;
            }

            if (!response?.ok) {
                const code = response?.code || 'SOCKET_AUTH_FAILED';
                console.warn('User socket auth failed:', code);
                finish(false);

                // Нельзя переиспользовать физическое соединение другого аккаунта.
                // Переподключение создаст новый handshake уже с актуальным JWT.
                if (code === 'SOCKET_IDENTITY_MISMATCH' && socket.connected) {
                    resetAuthenticatedState();
                    socket.disconnect();
                    socket.connect();
                }

                return;
            }

            authenticatedSocketId = socket.id;
            authenticatedToken = token;

            // BACK сам берёт identity из проверенного JWT.
            socket.emit('user:join');
            finish(true);
        });
    });

    authPromise = pending;

    pending.finally(() => {
        if (authPromise === pending) authPromise = null;
    });

    return pending;
};

const bindSocketLifecycle = (socket) => {
    if (!socket || socket[SOCKET_LIFECYCLE_MARK]) return;

    socket[SOCKET_LIFECYCLE_MARK] = true;

    socket.on('connect', () => {
        resetAuthenticatedState();
        applySocketAuth(socket);
        authenticateConnectedSocket(socket, { force: true });
    });

    socket.on('disconnect', () => {
        resetAuthenticatedState();
    });

    socket.on('connect_error', (error) => {
        console.warn('User socket connect_error:', error?.message || error);
    });
};

export const getSocket = () => {
    const socketConfigKey = getSocketConfigKey();
    const clientId = getSocketClientId();

    if (!socketInstance) {
        socketInstance = adoptExistingWindowSocket(socketConfigKey);
    }

    if (
        socketInstance &&
        typeof window !== 'undefined' &&
        window.__USER_SOCKET_CONFIG_KEY__ &&
        window.__USER_SOCKET_CONFIG_KEY__ !== socketConfigKey
    ) {
        socketInstance.disconnect();
        socketInstance = null;
        resetAuthenticatedState();
        clearGlobalSocket();
    }

    if (!socketInstance) {
        socketInstance = io(wsApi, {
            // Polling — безопасный fallback. После успешного старта Socket.IO
            // сам обновит транспорт до WebSocket, если upgrade доступен.
            transports: ['polling', 'websocket'],
            upgrade: true,
            rememberUpgrade: false,
            path: socketPath,
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 600,
            reconnectionDelayMax: 4000,
            timeout: 15000,
            withCredentials: false,
            auth: {
                token: getToken() || undefined,
                clientId,
            },
            query: { clientId },
        });

        if (typeof window !== 'undefined') {
            window.userSocket = socketInstance;
            window.__USER_SOCKET__ = socketInstance;
            window.__USER_SOCKET_CONFIG_KEY__ = socketConfigKey;
        }
    } else {
        applySocketAuth(socketInstance);
    }

    bindSocketLifecycle(socketInstance);

    return socketInstance;
};

export const joinSocketUser = () => {
    const socket = getSocket();
    const authChanged = applySocketAuth(socket);
    const token = getToken();

    if (!token) {
        resetAuthenticatedState();
        return;
    }

    if (authChanged) resetAuthenticatedState();

    if (!socket.connected) {
        socket.connect();
        return;
    }

    authenticateConnectedSocket(socket, { force: authChanged });
};

export const reconnectSocketUser = () => {
    const socket = getSocket();

    resetAuthenticatedState();
    applySocketAuth(socket);

    if (socket.connected) socket.disconnect();
    socket.connect();
};

export const socket = getSocket();
